import {Badge, Box, Group, Paper, Text, Title} from '@mantine/core'
import {IconChevronRight} from '@tabler/icons-react'
import {Link} from '@tanstack/react-router'

import {pythonWeekdaysToJs} from '../service-schedules/weekday-conversion'
import classes from './project-schedules-band.module.css'
import {
    formatScheduleCadenceLabel,
    formatScheduleCoverageLabel,
    formatScheduleWindowLabel,
} from './project-schedules-label'
import type {ProjectJobSummary, ProjectScheduleSummary} from './schemas'

type ProjectSchedulesBandProps = {
    projectId: string
    schedules: ProjectScheduleSummary[]
    jobs: ProjectJobSummary[]
}

const DAY_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

/** Cadence drawn as seven day cells so patterns compare down the column. */
function CadenceDayPills({jsWeekdays}: {jsWeekdays: number[]}) {
    return (
        <Box
            className={classes['dayPills']}
            role="img"
            aria-label={`Cadence: ${formatScheduleCadenceLabel(jsWeekdays)}`}
        >
            {DAY_INITIALS.map((initial, day) => (
                <Box
                    key={day}
                    aria-hidden="true"
                    className={`${classes['dayPill']} ${
                        jsWeekdays.includes(day) ? classes['dayPillActive'] : ''
                    }`}
                >
                    {initial}
                </Box>
            ))}
        </Box>
    )
}

function ScheduleRow({projectId, schedule}: {projectId: string; schedule: ProjectScheduleSummary}) {
    const windowLabel = formatScheduleWindowLabel(schedule.startDate, schedule.endDate)
    const jobLabel = schedule.governedJobCount === 1 ? 'job' : 'jobs'

    return (
        <Link
            to="/projects/$projectId/schedules/$scheduleId"
            params={{projectId, scheduleId: schedule.id}}
            className={classes['row']}
        >
            <Group gap="xs" wrap="nowrap" miw={0}>
                <Text size="xs" fw={700} ff="monospace" c="var(--mt-identity-schedule)">
                    {schedule.deployJobDisplayId}
                </Text>
                <Text fw={600} c="var(--mantine-color-anchor)" truncate>
                    {schedule.serviceTypeName}
                </Text>
            </Group>
            <Text size="sm">{windowLabel}</Text>
            <CadenceDayPills jsWeekdays={pythonWeekdaysToJs(schedule.cadenceWeekdays)} />
            <Text size="sm">
                <Text span fw={700} inherit>
                    {schedule.governedJobCount}
                </Text>{' '}
                {jobLabel}
            </Text>
            <Text size="xs" c="dimmed" truncate title={schedule.poNumber ?? 'No PO'}>
                PO: {schedule.poNumber ?? 'No PO'}
            </Text>
            <IconChevronRight
                size={16}
                className={classes['chevron']}
                color="var(--mt-text-muted)"
                aria-hidden="true"
            />
        </Link>
    )
}

export function ProjectSchedulesBand({projectId, schedules, jobs}: ProjectSchedulesBandProps) {
    if (schedules.length === 0) {
        return null
    }

    const scheduledJobCount = jobs.filter((job) => job.serviceScheduleId != null).length

    return (
        <Paper withBorder radius="md" className={classes['card']}>
            <Group gap="sm" align="center" className={classes['header']}>
                <Title order={4}>Schedules</Title>
                <Badge variant="light" color="gray">
                    {schedules.length}
                </Badge>
            </Group>

            {schedules.map((schedule) => (
                <ScheduleRow key={schedule.id} projectId={projectId} schedule={schedule} />
            ))}

            {jobs.length > 0 ? (
                <Box className={classes['footer']}>
                    <Text size="sm" c="dimmed">
                        {formatScheduleCoverageLabel(jobs.length, scheduledJobCount)}
                    </Text>
                </Box>
            ) : null}
        </Paper>
    )
}
